import type { ReactNode, ReactElement } from 'react';
import { Children } from 'react';

import BpkModal from '@skyscanner-internal/backpack-web/bpk-component-modal';
import { GLOBAL } from 'saddlebag-browser';

import NavigationButton, { NAVIGATION_BUTTON_TYPES } from '../NavigationButton';

import STYLES from './ModalContainer.module.scss';

const $document = GLOBAL.getDocument();

interface ModalContainerProps {
  ariaLabel?: string;
  ariaLabelBack?: string;
  ariaLabelClose: string;
  children: ReactNode;
  closeOnScrimClick?: boolean;
  handleBack: () => void;
  handleClose: () => void;
  isOpen: boolean;
  shouldShowBack?: boolean;
  shouldShowClose?: boolean;
}

interface SlotProps {
  children: ReactNode;
}

const PrimaryContent = ({ children }: SlotProps) => (
  <div className={STYLES['primary-content']}>{children}</div>
);

const SecondaryContent = ({ children }: SlotProps) => (
  <div className={STYLES['secondary-content']}>{children}</div>
);

const ModalContainer = ({
  ariaLabel,
  ariaLabelBack,
  ariaLabelClose,
  children,
  closeOnScrimClick,
  handleBack,
  handleClose,
  isOpen,
  shouldShowBack,
  shouldShowClose,
}: ModalContainerProps) => {
  const slots: { primary: ReactNode; secondary: ReactNode } = { primary: null, secondary: null };

  Children.forEach(children, (child) => {
    if ((child as ReactElement)?.type === PrimaryContent) {
      slots.primary = child;
    } else if ((child as ReactElement)?.type === SecondaryContent) {
      slots.secondary = child;
    }
  });

  return (
    <BpkModal
      isOpen={isOpen}
      showHeader={false}
      onClose={handleClose}
      id="login-modal"
      getApplicationElement={() => $document.getElementById('pagewrap')}
      renderTarget={() => $document.getElementById('modal-container')}
      closeOnScrimClick={closeOnScrimClick}
      ariaLabel={ariaLabel}
      className={STYLES['modal-wrapper']}
      padded={false}
      fullScreenOnMobile={false}
    >
      <div className={STYLES['modal-container']}>
        <div className={STYLES['modal-container__navigation']}>
          {shouldShowClose && (
            <NavigationButton
              ariaLabel={ariaLabelClose}
              handleClick={handleClose}
              type={NAVIGATION_BUTTON_TYPES.CLOSE}
            />
          )}
          {shouldShowBack && (
            <NavigationButton
              ariaLabel={ariaLabelBack}
              handleClick={handleBack}
              type={NAVIGATION_BUTTON_TYPES.BACK}
            />
          )}
        </div>
        {slots.primary}
        {slots.secondary}
      </div>
    </BpkModal>
  );
};

ModalContainer.PrimaryContent = PrimaryContent;
ModalContainer.SecondaryContent = SecondaryContent;

export default ModalContainer;
